<template>
    <div>
        <HeaderContent :title="isEdit ? 'Edit Vehicle Maintenance' : 'New Vehicle Maintenance'" backLink="/vehicle-maintenance" />

        <BForm @submit.stop.prevent>
            <BRow>
                <BCol cols="12">
                     <!-- 1. INFO SECTION -->
                    <!-- Matches SpjGeneralInfo style: border-info shadow-sm -->
                    <BCard class="mb-3 border-info shadow-sm">
                         <template #header>
                            <h5 class="mb-0 text-info font-weight-bold text-left">1. Vehicle Information</h5>
                        </template>
                        <div class="p-2">
                             <BRow>
                                <BCol md="6" class="text-left">
                                    <BFormGroup label="Vehicle *" label-for="vehicle" class="mb-3">
                                        <v-select 
                                            v-model="form.vehicle_uuid" 
                                            :options="vehicles"
                                            :reduce="v => v.uuid" 
                                            :get-option-label="option => `${option.name} (${option.plate_number})`"
                                            placeholder="Select Vehicle" 
                                            style="background-color: white"
                                            :class="{ 'is-invalid': v$.form.vehicle_uuid.$dirty && v$.form.vehicle_uuid.$invalid }"
                                            @option:selected="onVehicleSelected"
                                         />
                                        <BFormInvalidFeedback :state="!(v$.form.vehicle_uuid.$dirty && v$.form.vehicle_uuid.$invalid)">
                                            {{ v$.form.vehicle_uuid.$errors[0]?.$message }}
                                        </BFormInvalidFeedback>
                                    </BFormGroup>

                                      <BFormGroup label="Driver *" label-for="staff" class="mb-3">
                                        <v-select 
                                            v-model="form.staff_uuid" 
                                            :options="staffs"
                                            :reduce="s => s.uuid" 
                                            label="fullname"
                                            placeholder="Select Driver" 
                                            style="background-color: white"
                                            :class="{ 'is-invalid': v$.form.staff_uuid.$dirty && v$.form.staff_uuid.$invalid }"
                                         />
                                        <BFormInvalidFeedback :state="!(v$.form.staff_uuid.$dirty && v$.form.staff_uuid.$invalid)">
                                            {{ v$.form.staff_uuid.$errors[0]?.$message }}
                                        </BFormInvalidFeedback>
                                    </BFormGroup>
                                </BCol>

                                <BCol md="6" class="text-left">
                                     <BFormGroup label="Service Date *" label-for="service_date" class="mb-3">
                                        <BFormInput type="date" v-model="form.service_date" 
                                            :class="{ 'is-invalid': v$.form.service_date.$dirty && v$.form.service_date.$invalid }" />
                                         <BFormInvalidFeedback :state="!(v$.form.service_date.$dirty && v$.form.service_date.$invalid)">
                                            {{ v$.form.service_date.$errors[0]?.$message }}
                                        </BFormInvalidFeedback>
                                    </BFormGroup>

                                     <BFormGroup label="Total KM *" label-for="total_km_service" class="mb-3">
                                        <BFormInput type="number" v-model="form.total_km_service" min="0"
                                            :class="{ 'is-invalid': v$.form.total_km_service.$dirty && v$.form.total_km_service.$invalid }" />
                                         <BFormInvalidFeedback :state="!(v$.form.total_km_service.$dirty && v$.form.total_km_service.$invalid)">
                                            {{ v$.form.total_km_service.$errors[0]?.$message }}
                                        </BFormInvalidFeedback>
                                    </BFormGroup>
                                </BCol>
                             </BRow>
                            <BRow>
                                <BCol md="6" class="text-left">
                                    <BFormGroup label="Payment Status *" label-for="payment_status" class="mb-3">
                                        <BFormSelect v-model="form.payment_status" :options="['Paid', 'Unpaid']" 
                                            :class="{ 'is-invalid': v$.form.payment_status.$dirty && v$.form.payment_status.$invalid }"/>
                                            <BFormInvalidFeedback :state="!(v$.form.payment_status.$dirty && v$.form.payment_status.$invalid)">
                                            {{ v$.form.payment_status.$errors[0]?.$message }}
                                        </BFormInvalidFeedback>
                                    </BFormGroup>
                                </BCol>
                                <BCol md="12" class="text-left">
                                    <BFormGroup label="Remarks" label-for="remarks" class="mb-3">
                                        <BFormTextarea v-model="form.remarks" placeholder="Enter remarks (optional)" rows="3" />
                                    </BFormGroup>
                                </BCol>
                            </BRow>
                        </div>
                    </BCard>

                    <!-- 2. SERVICE ITEM SECTION -->
                     <BCard class="mb-3 border-info shadow-sm">
                         <template #header>
                            <h5 class="mb-0 text-info font-weight-bold text-left">2. Service Items</h5>
                        </template>
                        <div class="p-2 table-responsive">
                             <table class="table table-bordered table-hover">
                                <thead class="bg-light">
                                    <tr>
                                        <th style="min-width: 150px;">Item Name</th>
                                        <th style="min-width: 150px;">Vendor</th>
                                        <th style="width: 100px;min-width: 100px;">Qty</th>
                                        <th style="width: 120px;min-width: 120px;">Unit</th>
                                        <th style="min-width: 150px;">Price</th>
                                        <th style="min-width: 50px;" class="text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in form.items" :key="item._key">
                                        <td>
                                            <BFormInput v-model="item.name" placeholder="Item Name" 
                                                :class="{ 'is-invalid': v$.form.$dirty && v$.form.items.$each.$response.$errors[index]?.name.length }" />
                                                <BFormInvalidFeedback class="text-left" :state="!(v$.form.$dirty && v$.form.items.$each.$response.$errors[index]?.name.length)">
                                            {{ v$.form.items.$each.$response.$errors[index]?.name[0]?.$message }}
                                        </BFormInvalidFeedback>
                                        </td>
                                        <td>
                                            <v-select 
                                                v-model="item.vendor_obj" 
                                                :options="vendors"
                                                label="name"
                                                taggable
                                                push-tags
                                                placeholder="Select Vendor"
                                                :create-option="(val) => ({ uuid: null, name: val })"
                                                @update:model-value="(val) => updateVendor(item, val)"
                                            />
                                        </td>
                                        <td>
                                            <BFormInput type="number" v-model="item.quantity" placeholder="1" min="1"
                                                :class="{ 'is-invalid': v$.form.$dirty && v$.form.items.$each.$response.$errors[index]?.quantity?.length }" />
                                            <BFormInvalidFeedback class="text-left" :state="!(v$.form.$dirty && v$.form.items.$each.$response.$errors[index]?.quantity?.length)">
                                                {{ v$.form.items.$each.$response.$errors[index]?.quantity?.[0]?.$message }}
                                            </BFormInvalidFeedback>
                                        </td>
                                        <td>
                                            <v-select 
                                                v-model="item.unit" 
                                                :options="unitOptions"
                                                taggable
                                                push-tags
                                                placeholder="Unit" 
                                                :class="{ 'is-invalid': v$.form.$dirty && v$.form.items.$each.$response.$errors[index]?.unit.length }"
                                            />
                                            <BFormInvalidFeedback class="text-left" :state="!(v$.form.$dirty && v$.form.items.$each.$response.$errors[index]?.unit.length)">
                                            {{ v$.form.items.$each.$response.$errors[index]?.unit[0]?.$message }}
                                        </BFormInvalidFeedback>
                                        </td>
                                        <td>
                                            <BFormInput type="number" v-model="item.price" placeholder="0" class="text-right"
                                                :class="{ 'is-invalid': v$.form.$dirty && v$.form.items.$each.$response.$errors[index]?.price.length }" />
                                                <BFormInvalidFeedback class="text-left" :state="!(v$.form.$dirty && v$.form.items.$each.$response.$errors[index]?.price.length)">
                                            {{ v$.form.items.$each.$response.$errors[index]?.price[0]?.$message }}
                                        </BFormInvalidFeedback>
                                        </td>
                                        <td class="text-center">
                                             <BButton variant="link" class="text-danger p-0" @click="removeItem(index)" v-if="form.items.length > 1">
                                                <i class="fas fa-trash"></i>
                                            </BButton>
                                        </td>
                                    </tr>
                                     
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colspan="4" class="text-right">
                                            Grand Total
                                        </td>
                                        <td colspan="1" class="h5 text-right font-weight-bold">
                                            {{ formatRupiah(grandTotal) }}
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                             </table>
                             <div class="d-flex justify-content-center mt-3">
                                <BButton type="button" variant="outline-primary" size="sm" @click="addItem">
                                    <i class="fas fa-plus"></i> Add Item
                                </BButton>
                             </div>
                        </div>
                    </BCard>

                     <div class="d-flex justify-content-end mb-5">
                         <BButton variant="danger" class="me-2" @click="router.back()"><i class="fas fa-times me-1"></i>Cancel</BButton>
                         <BButton variant="primary" @click="submitForm">
                            <i class="fas fa-save me-1"></i> {{ isEdit ? 'Update Vehicle Maintenance' : 'Save Vehicle Maintenance' }}
                         </BButton>
                     </div>

                </BCol>
            </BRow>
        </BForm>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { useAuthStore } from "@/store/auth.js";
import { useToast } from "vue-toastification";
import { useLoading } from "vue-loading-overlay";
import HeaderContent from '@/components/HeaderContent.vue';
import useVuelidate from '@vuelidate/core';
import { required, minValue, helpers } from '@vuelidate/validators';
import { BFormInvalidFeedback, BFormInput, BButton, BForm, BRow, BCol, BCard, BFormGroup, BFormSelect, BFormTextarea } from 'bootstrap-vue-next';
import vSelect from 'vue-select';
import moment from 'moment';

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const toast = useToast();
const $loading = useLoading();

const isEdit = computed(() => !!route.params.uuid);
const vehicles = ref([]);
const staffs = ref([]);
const vendors = ref([]);
const unitOptions = ['Liter', 'Set', 'Pcs', 'ml'];

const form = reactive({
    vehicle_uuid: null,
    staff_uuid: null,
    service_date: new Date().toISOString().split('T')[0],
    total_km_service: 0,
    payment_status: 'Unpaid',
    remarks: '',
    items: [
        { _key: Date.now(), name: '', quantity: 1, unit: '', price: '', vendor_uuid: null, vendor_name: '', vendor_obj: null }
    ]
});

const rules = {
    form: {
        vehicle_uuid: { 
            required: helpers.withMessage('Vehicle is required', required),
         },
        staff_uuid: { 
            required: helpers.withMessage('Staff is required', required),
         },
        service_date: { 
            required: helpers.withMessage('Service date is required', required),
         },
        total_km_service: { 
            required: helpers.withMessage('Total KM is required', required),
            minValue: helpers.withMessage('Total KM must be greater than or equal to 0', minValue(0))
         },
        payment_status: { 
            required: helpers.withMessage('Payment status is required', required),
         },
        items: {
            required,
            $each: helpers.forEach({
                name: { 
                    required: helpers.withMessage('Name is required', required) 
                },
                quantity: { 
                    required: helpers.withMessage('Qty is required', required),
                    minValue: helpers.withMessage('Qty must be at least 1', minValue(1)) 
                },
                unit: { 
                    required: helpers.withMessage('Unit is required', required) 
                },
                price: { 
                    required: helpers.withMessage('Price is required', required),
                    minValue: helpers.withMessage('Price must be greater than or equal to 0', minValue(0)) 
                }
            })
        }
    }
};

const v$ = useVuelidate(rules, { form });

const grandTotal = computed(() => {
    return form.items.reduce((sum, item) => sum + (Number(item.price || 0)), 0);
});

const formatRupiah = (number) => {
    return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
    }).format(number);
};

const addItem = () => {
    form.items.push({ _key: Date.now() + Math.random(), name: '', quantity: 1, unit: '', price: '', vendor_uuid: null, vendor_name: '', vendor_obj: null });
};

const removeItem = (index) => {
    form.items.splice(index, 1);
};

const updateVendor = (item, val) => {
    if (val) {
        item.vendor_uuid = val.uuid;
        item.vendor_name = val.name;
    } else {
        item.vendor_uuid = null;
        item.vendor_name = '';
    }
};

const loadVehicles = async () => {
    try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/vehicle`, {
             headers: { Authorization: "Bearer " + auth._token }
        });
        if (response.data.status) {
            vehicles.value = response.data.data;
        }
    } catch (e) {
        console.error("Failed to load vehicles", e);
    }
};

const loadStaffs = async () => {
    try { 
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/staff`, {
             headers: { Authorization: "Bearer " + auth._token }
        });
        if (response.data.status) {
            staffs.value = response.data.data.filter(s => s.position === 'Driver');
        }
    } catch (e) {
        console.error("Failed to load staffs", e);
    }
};

const loadVendors = async () => {
    try { 
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/vendor`, {
             headers: { Authorization: "Bearer " + auth._token }
        });
        if (response.data.status) {
            vendors.value = response.data.data;
        }
    } catch (e) {
        console.error("Failed to load vendors", e);
    }
};


const onVehicleSelected = (vehicle) => {
    if (vehicle) {
        form.total_km_service = vehicle.total_km || 0;
    }
};

const loadDetail = async () => {
    const loader = $loading.show({ color: "#489464", loader: "spinner" });
    try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/vehicle-maintenance/detail/${route.params.uuid}`, {
            headers: { Authorization: "Bearer " + auth._token }
        });
        const data = response.data.data;
        form.vehicle_uuid = data.vehicle_uuid;
        form.staff_uuid = data.staff_uuid;
        form.service_date = data.service_date ? moment(data.service_date).format('YYYY-MM-DD') : '';
        form.total_km_service = data.total_km_service; 
        form.payment_status = data.payment_status;
        form.remarks = data.remarks || '';
        form.items = data.items.map(i => {
            const vName = i.vendor_name || (i.vendor ? i.vendor.name : '');
            let vObj = null;
            if (i.vendor_uuid) {
               const found = vendors.value.find(v => v.uuid === i.vendor_uuid);
               vObj = found ? found : { uuid: i.vendor_uuid, name: vName };
            } else if (vName) {
               vObj = { uuid: null, name: vName };
            }

            return {
                _key: Math.random(),
                name: i.name,
                quantity: i.quantity,
                unit: i.unit,
                price: i.price,
                vendor_uuid: i.vendor_uuid,
                vendor_name: vName,
                vendor_obj: vObj
            };
        });

    } catch (error) {
        toast.error("Failed to load detail");
        router.push('/vehicle-maintenance');
    } finally {
        loader.hide();
    }
};

const submitForm = async () => {
    const isFormCorrect = await v$.value.$validate();
    if (!isFormCorrect) {
        toast.error("Please fill in all required fields.");
        return;
    }

    const loader = $loading.show({ color: "#489464", loader: "spinner" });
    try {
        const url = isEdit.value 
            ? `${process.env.VUE_APP_API_URL}/vehicle-maintenance/edit/${route.params.uuid}`
            : `${process.env.VUE_APP_API_URL}/vehicle-maintenance/add`;
        
        const method = isEdit.value ? 'put' : 'post';

        const response = await axios[method](url, form, {
            headers: { Authorization: "Bearer " + auth._token }
        });

        if (response.status === 200 || response.status) {
             toast.success(isEdit.value ? "Updated successfully" : "Created successfully");
             router.push('/vehicle-maintenance');
        }

    } catch (error) {
        toast.error(error.response?.data?.message || "Failed to save");
    } finally {
        loader.hide();
    }
};

onMounted(async () => {
    await Promise.all([loadVehicles(), loadStaffs(), loadVendors()]);
    if (isEdit.value) {
        loadDetail();
    }
});
</script>
